import React from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import AdminQueuePage from './pages/AdminQueuePage';
import BookingsListPage from './pages/BookingsListPage';
import BookingProgressPage from './pages/BookingProgressPage';
import BookingTypePage from './pages/BookingTypePage';
import ChangeNamePage from './pages/ChangeNamePage';
import ConcertRequestPage from './pages/ConcertRequestPage';
import CorporateRequestPage from './pages/CorporateRequestPage';
import EditProfilePage from './pages/EditProfilePage';
import DashboardPage from './pages/DashboardPage';
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ProfilePage from './pages/ProfilePage';
import HelpSupportPage from './pages/HelpSupportPage';
import SecurityPage from './pages/SecurityPage';
import TermsPolicyPage from './pages/TermsPolicyPage';
import SignupPage from './pages/SignupPage';
import NotificationsPage from './pages/NotificationsPage';
import PaymongoTest from './pages/PaymongoTest';
import BottomNav from './components/BottomNav';
import TopBar from './components/TopBar';
import PwaInstallOverlays from './components/PwaInstallOverlays';
import { useAuthSession } from './hooks/useAuthSession';
import { PwaInstallProvider } from './hooks/usePwaInstall';

function AppContent() {
  const location = useLocation();
  const { isAuthenticated, loading: authLoading } = useAuthSession();

  const isAuthPage =
    location.pathname === '/login' ||
    location.pathname === '/signup' ||
    location.pathname === '/reset-password' ||
    location.pathname === '/profile/security' ||
    location.pathname === '/profile/help' ||
    location.pathname === '/profile/terms';

  const showTopBar = !isAuthPage;
  const showBottomNav = !isAuthPage && !authLoading && isAuthenticated;
  const mainPaddingClass = showBottomNav
    ? 'pt-6 pb-24 md:pt-8 md:pb-28'
    : 'pt-6 pb-8 md:pt-8 md:pb-10';

  return (
    <div className="h-dvh overflow-hidden bg-[#141414] flex flex-col">
      {showTopBar && <TopBar isAuthenticated={isAuthenticated} authLoading={authLoading} />}

      <div className="flex-1 overflow-y-auto">
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/profile/security" element={<SecurityPage />} />
          <Route path="/profile/help" element={<HelpSupportPage />} />
          <Route path="/profile/terms" element={<TermsPolicyPage />} />
          <Route path="/test-paymongo" element={<PaymongoTest />} />
          <Route
            path="*"
            element={
              <main className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ${mainPaddingClass}`}>
                {authLoading ? (
                  <div className="flex items-center justify-center py-16">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-gold mx-auto mb-3" />
                      <p className="text-slate-300 text-sm">Loading...</p>
                    </div>
                  </div>
                ) : (
                  <Routes>
                    <Route
                      path="/"
                      element={
                        isAuthenticated
                          ? <Navigate to="/dashboard" replace />
                          : <LandingPage />
                      }
                    />
                    <Route path="/book" element={<BookingsListPage />} />
                    <Route path="/book/progress/:bookingId" element={<BookingProgressPage />} />
                    <Route path="/book/select" element={<BookingTypePage />} />
                    <Route path="/book/concert" element={<ConcertRequestPage />} />
                    <Route path="/book/corporate" element={<CorporateRequestPage />} />
                    <Route path="/dashboard" element={<DashboardPage />} />
                    <Route path="/notifications" element={<NotificationsPage />} />
                    <Route path="/profile" element={<ProfilePage />} />
                    <Route path="/profile/edit" element={<EditProfilePage />} />
                    <Route path="/profile/change-name" element={<ChangeNamePage />} />
                    <Route path="/admin" element={<AdminQueuePage />} />
                    <Route
                      path="*"
                      element={<Navigate to={isAuthenticated ? '/dashboard' : '/'} replace />}
                    />
                  </Routes>
                )}
              </main>
            }
          />
        </Routes>
      </div>

      {showBottomNav && <BottomNav />}
      <PwaInstallOverlays />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <PwaInstallProvider>
        <AppContent />
      </PwaInstallProvider>
    </BrowserRouter>
  );
}

export default App;
